import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandSnapchat,
  IconBrandTiktok,
  IconBrandX,
  IconBrandYoutube,
  IconCopy,
  IconExternalLink,
  IconUserOff,
  IconWorld,
} from "@tabler/icons-react";
import { toast } from "react-hot-toast";

import PageHeader from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { IconButton } from "@/components/dashboard/icon-button";
import { FactRow } from "@/components/dashboard/fact-row";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import {
  describeChange,
  MetricPanel,
  MetricStripSkeleton,
  type Metric,
} from "@/components/dashboard/metric-cell";
import { ProductIdentity } from "@/components/ProductTable/ProductTable";
import { Flag } from "@/pages/brand/dashboard/affiliate-map";
import { countryName } from "@/pages/brand/dashboard/country-geometry";
import { brandService, type BrandAffiliateOverview } from "@/services/brand.service";
import { formatSocialMediaUrl } from "@/utils/socialMediaUtils";
import { formatAmount, formatDate, formatNumber, formatstorageUrl } from "@/utils/formatters";
import { cn } from "@/lib/utils";

const LIST_PATH = "/brand/affiliates/active";

/** The old overview endpoint has no brand scoping and none of the extra
 *  sections. Until the new one is deployed, the page shows what it can. */
type Overview = BrandAffiliateOverview & { partial?: boolean };

function isNotFound(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 404;
}

function fromLegacy(
  legacy: Awaited<ReturnType<typeof brandService.getAffiliateOverview>>,
  days: number
): Overview {
  const a = legacy.affiliate;
  const revenue = legacy.analytics?.revenue;
  const orders = legacy.analytics?.orders;
  const commission = legacy.analytics?.commision;
  const current = {
    revenue: revenue?.totalSales ?? 0,
    orders: orders?.orders ?? 0,
    commission: commission?.commision ?? 0,
  };
  return {
    partial: true,
    period: { from: "", to: "", days },
    affiliate: {
      id: a.id,
      firstName: a.firstName,
      lastName: a.lastName,
      email: a.email,
      username: a.username,
      country: a.country,
      profileImage: a.profileImage,
      bio: a.bio,
      createdAt: "",
      niches: a.niches.map((n) => ({ id: n.id, name: n.name })),
      socials: a.AffiliateSocialMedia.map((s) => ({
        id: s.id,
        platform: s.socialMediaPlatform.name,
        username: s.username,
      })),
    },
    enrollments: [],
    current: {
      ...current,
      averageOrder: current.orders ? current.revenue / current.orders : 0,
    },
    previous: {
      revenue: revenue?.previousRevenue ?? 0,
      orders: orders?.previousOrders ?? 0,
      commission: commission?.previousCommission ?? 0,
    },
    byDay: {
      revenue: revenue?.salesByDay ?? {},
      orders: orders?.orderByDay ?? {},
      commission: commission?.commisionByDay ?? {},
    },
    allTime: {
      revenue: 0,
      orders: 0,
      commission: 0,
      averageOrder: 0,
      firstSaleAt: null,
      lastSaleAt: null,
      shareOfPeriodRevenue: 0,
    },
    payouts: { paid: 0, pending: 0, lastPaidAt: null },
    topProducts: [],
    recentSales: [],
  };
}

const SOCIAL_ICONS: Record<string, typeof IconBrandInstagram> = {
  FACEBOOK: IconBrandFacebook,
  INSTAGRAM: IconBrandInstagram,
  TIKTOK: IconBrandTiktok,
  YOUTUBE: IconBrandYoutube,
  SNAPCHAT: IconBrandSnapchat,
  X: IconBrandX,
  TWITTER: IconBrandX,
};

function SocialLink({ platform, username }: { platform: string; username: string }) {
  const Icon = SOCIAL_ICONS[platform.toUpperCase()] ?? IconWorld;
  const href = formatSocialMediaUrl(username, platform);
  const label = `${platform.charAt(0)}${platform.slice(1).toLowerCase()}: ${username}`;
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon size={16} stroke={1.75} />
    </a>
  );
}

/** Bio comes from the affiliate as HTML; the brand needs the words only. */
function plainText(html: string | null | undefined) {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function shortDate(value: string | null | undefined) {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const salesColumns: ColumnDef<Overview["recentSales"][number]>[] = [
  {
    accessorKey: "orderNumber",
    header: "Order",
    meta: { width: "w-[12%]" },
    cell: ({ row }) => (
      <span className="font-medium tabular-nums text-foreground">#{row.original.orderNumber}</span>
    ),
  },
  {
    accessorKey: "programName",
    header: "Program",
    meta: { width: "w-[22%]" },
    cell: ({ row }) => (
      <span className="block truncate text-foreground" title={row.original.programName}>
        {row.original.programName}
      </span>
    ),
  },
  {
    accessorKey: "totalAmount",
    header: "Order total",
    meta: { width: "w-[16%]" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">{formatAmount(row.original.totalAmount)}</span>
    ),
  },
  {
    accessorKey: "commissionAmount",
    header: "Commission",
    meta: { width: "w-[16%]" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">{formatAmount(row.original.commissionAmount)}</span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { width: "w-[14%]" },
    cell: ({ row }) => <StatusText status={row.original.status} />,
  },
  {
    accessorKey: "createdAt",
    header: "Date",
    meta: { width: "w-[14%]" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">{shortDate(row.original.createdAt)}</span>
    ),
  },
  {
    id: "open",
    header: "",
    meta: { width: "w-[6%]", cellClassName: "text-right", skeleton: <span /> },
    cell: ({ row }) =>
      row.original.orderStatusUrl ? (
        <IconButton
          label="Open order in Shopify"
          icon={<IconExternalLink stroke={1.75} />}
          onClick={() => window.open(row.original.orderStatusUrl!, "_blank", "noopener,noreferrer")}
        />
      ) : null,
  },
];

const productColumns: ColumnDef<Overview["topProducts"][number]>[] = [
  {
    accessorKey: "title",
    header: "Product",
    cell: ({ row }) => <ProductIdentity title={row.original.title} image={row.original.image} />,
  },
  {
    accessorKey: "units",
    header: "Units",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums text-foreground">{formatNumber(row.original.units)}</span>,
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums font-medium text-foreground">{formatAmount(row.original.revenue)}</span>
    ),
  },
];

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <div className="animate-pulse space-y-3">
        <div className="h-4 w-16 rounded bg-muted" />
        <div className="h-7 w-56 rounded bg-muted" />
      </div>
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
          <div className="h-4 w-24 rounded bg-muted" />
          <div className="h-6 w-40 rounded-md bg-muted" />
        </div>
        <div className="flex items-center gap-4 px-5 pb-4 animate-pulse">
          <div className="size-14 rounded-full bg-muted" />
          <div className="space-y-2">
            <div className="h-4 w-40 rounded bg-muted" />
            <div className="h-3 w-72 rounded bg-muted" />
          </div>
        </div>
        <MetricStripSkeleton count={4} columns="lg:grid-cols-4" />
      </div>
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="rounded-lg border border-border bg-card lg:col-span-8">
          <div className="px-5 pt-4 pb-3 animate-pulse">
            <div className="h-4 w-24 rounded bg-muted" />
          </div>
          <div className="px-3 pb-3">
            <DataTable columns={salesColumns} data={[]} isLoading skeletonRows={6} minTableWidth="min-w-0" />
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card lg:col-span-4">
          <div className="px-5 pt-4 pb-3 animate-pulse">
            <div className="h-4 w-20 rounded bg-muted" />
          </div>
          <div className="space-y-4 px-5 pb-4 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex justify-between">
                <div className="h-3.5 w-28 rounded bg-muted" />
                <div className="h-4 w-16 rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AffiliateDetail() {
  const { id } = useParams();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);

  const load = useCallback(
    async (period: number, initial: boolean) => {
      if (!id) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      setFailed(false);
      try {
        setData(await brandService.getBrandAffiliateOverview(id, period));
      } catch (error) {
        if (!isNotFound(error)) {
          console.error("Failed to load affiliate:", error);
          setFailed(true);
        } else {
          // The scoped endpoint is not deployed yet, or the affiliate is not
          // one of this brand's. The legacy call tells them apart.
          try {
            const end = new Date();
            const start = new Date(end.getTime() - period * 86400000);
            const legacy = await brandService.getAffiliateOverview(
              id,
              start.toISOString().slice(0, 10),
              end.toISOString().slice(0, 10),
              period
            );
            setData(fromLegacy(legacy, period));
          } catch (legacyError) {
            if (isNotFound(legacyError)) setNotFound(true);
            else setFailed(true);
          }
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id]
  );

  useEffect(() => {
    load(30, true);
  }, [load]);

  const changePeriod = (next: number) => {
    if (next === days || refreshing) return;
    setDays(next);
    load(next, false);
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied");
    } catch {
      toast.error("Could not copy the code");
    }
  };

  const enrollmentColumns = useMemo<ColumnDef<Overview["enrollments"][number]>[]>(
    () => [
      {
        accessorKey: "programName",
        header: "Program",
        cell: ({ row }) => (
          <span className="block max-w-[14rem] truncate font-medium text-foreground" title={row.original.programName}>
            {row.original.programName}
          </span>
        ),
      },
      {
        accessorKey: "code",
        header: "Code",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1 text-foreground">
            {row.original.code}
            <IconButton
              label="Copy code"
              icon={<IconCopy stroke={1.75} />}
              onClick={() => void copyCode(row.original.code)}
            />
          </span>
        ),
      },
      {
        accessorKey: "orders",
        header: "Orders",
        meta: { fit: true },
        cell: ({ row }) => <span className="tabular-nums text-foreground">{formatNumber(row.original.orders)}</span>,
      },
      {
        accessorKey: "revenue",
        header: "Revenue",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums font-medium text-foreground">{formatAmount(row.original.revenue)}</span>
        ),
      },
      {
        accessorKey: "joinedAt",
        header: "Joined",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.original.joinedAt)}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        meta: { fit: true },
        cell: ({ row }) => <StatusText status={row.original.status} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (loading) return <DetailSkeleton />;

  if (notFound || (!data && !failed)) {
    return (
      <div className="space-y-4">
        <PageHeader title="Affiliate" closeTo={LIST_PATH} />
        <EmptyState
          icon={<IconUserOff stroke={1.75} />}
          title="Affiliate not found"
          body="They may have left your programs or the link is out of date"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <PageHeader title="Affiliate" closeTo={LIST_PATH} />
        <EmptyState
          icon={<IconUserOff stroke={1.75} />}
          title="Could not load this affiliate"
          body="Check your connection and try again"
          action={
            <Button variant="outline" onClick={() => void load(days, true)}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  const { affiliate, current, previous, byDay, allTime, payouts, enrollments } = data;
  const name = [affiliate.firstName, affiliate.lastName].filter(Boolean).join(" ").trim() || "Affiliate";
  const bio = plainText(affiliate.bio);
  const niches = affiliate.niches.map((n) => n.name).join(", ");
  const primaryStatus = enrollments.find((e) => e.status === "APPROVED")?.status ?? enrollments[0]?.status;

  const metrics: Metric[] = [
    {
      id: "affiliate-revenue",
      label: "Revenue",
      value: formatAmount(current.revenue),
      change: describeChange(current.revenue, previous.revenue, days, formatAmount),
      series: byDay.revenue,
      unit: "revenue",
      format: formatAmount,
    },
    {
      id: "affiliate-orders",
      label: "Orders",
      value: formatNumber(current.orders),
      change: describeChange(current.orders, previous.orders, days, formatNumber),
      series: byDay.orders,
      unit: "order",
    },
    {
      id: "affiliate-commission",
      label: "Commission",
      value: formatAmount(current.commission),
      change: describeChange(current.commission, previous.commission, days, formatAmount),
      series: byDay.commission,
      unit: "commission",
      format: formatAmount,
    },
    {
      id: "affiliate-average-order",
      label: "Average order",
      value: formatAmount(current.averageOrder),
      change: data.partial
        ? undefined
        : allTime.orders > 0
          ? `All time: ${formatAmount(allTime.averageOrder)}`
          : "No orders yet",
    },
  ];

  const facts = [
    affiliate.email,
    affiliate.username ? `@${affiliate.username}` : null,
    affiliate.createdAt ? `Joined ${formatDate(affiliate.createdAt)}` : null,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-4">
      <PageHeader
        title={name}
        closeTo={LIST_PATH}
        customActions={[
          {
            id: "email-affiliate",
            label: "Copy email",
            onClick: () => {
              // Not a mailto: link. That hands off to the OS, and on a machine
              // with no mail client it shows a "open with" prompt and nothing
              // else, which reads as a broken button. Copying always works.
              navigator.clipboard
                .writeText(affiliate.email)
                .then(() => toast.success(`Copied ${affiliate.email}`))
                .catch(() => toast.error("Couldn't copy the email address"));
            },
          },
        ]}
      />

      <MetricPanel
        id="affiliate-performance"
        title="Performance"
        metrics={metrics}
        columns="lg:grid-cols-4"
        className={cn("transition-opacity", refreshing && "opacity-60")}
        action={<PeriodToggle value={days} onChange={changePeriod} disabled={refreshing} />}
      >
        <div className="flex flex-wrap items-start gap-4 px-5 pb-4">
          <UserAvatar
            src={formatstorageUrl(affiliate.profileImage ?? "") || null}
            name={name}
            className="size-14 text-lg"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[15px] font-semibold text-foreground">{name}</span>
              {primaryStatus && <StatusText status={primaryStatus} />}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
              {affiliate.country && (
                <span className="inline-flex items-center gap-1.5">
                  <Flag code={affiliate.country} />
                  {countryName(affiliate.country)}
                </span>
              )}
              {affiliate.country && facts.length > 0 && <span aria-hidden>·</span>}
              {facts.map((fact, i) => (
                <span key={fact} className="inline-flex items-center gap-1.5">
                  {i > 0 && <span aria-hidden>·</span>}
                  {fact}
                </span>
              ))}
            </div>
            {(niches || affiliate.socials.length > 0) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {niches && <span>{niches}</span>}
                {affiliate.socials.length > 0 && (
                  <span className="inline-flex items-center gap-2">
                    {affiliate.socials.map((s) => (
                      <SocialLink key={s.id} platform={s.platform} username={s.username} />
                    ))}
                  </span>
                )}
              </div>
            )}
            {bio && (
              <p className={cn("mt-2 max-w-2xl text-sm text-muted-foreground", !bioOpen && "line-clamp-2")}>
                {bio}
                {bioOpen && (
                  <>
                    {" "}
                    <button
                      type="button"
                      onClick={() => setBioOpen(false)}
                      className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      Less
                    </button>
                  </>
                )}
              </p>
            )}
            {bio && bio.length > 160 && !bioOpen && (
              <button
                type="button"
                onClick={() => setBioOpen(true)}
                className="mt-1 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                More
              </button>
            )}
          </div>
        </div>
      </MetricPanel>

      {!data.partial && (
        <>
          <div className="grid gap-4 lg:grid-cols-12">
            <DashboardCard
              id="affiliate-recent-sales"
              title="Recent sales"
              className="lg:col-span-8"
              bodyClassName="px-3 pb-3"
            >
              <DataTable
                columns={salesColumns}
                data={data.recentSales}
                getRowId={(row) => row.id}
                emptyText="No sales yet"
                minTableWidth="min-w-[44rem]"
                maxHeight="max-h-[26rem]"
              />
              {data.recentSales.length >= 20 && (
                <div className="mt-3 border-t border-border px-2 pt-3 text-xs text-muted-foreground">
                  Last 20 sales
                </div>
              )}
            </DashboardCard>

            <DashboardCard id="affiliate-standing" title="Standing" className="lg:col-span-4" bodyClassName="px-5 pb-2">
              <FactRow label="All-time revenue" value={formatAmount(allTime.revenue)} />
              <FactRow label="All-time orders" value={formatNumber(allTime.orders)} />
              <FactRow label="Commission earned" value={formatAmount(allTime.commission)} />
              <FactRow
                label="Share of revenue"
                value={`${Math.round(allTime.shareOfPeriodRevenue * 100)}%`}
                note={`last ${days} days`}
              />
              <FactRow
                label="Paid out"
                value={formatAmount(payouts.paid)}
                note={payouts.lastPaidAt ? `last ${shortDate(payouts.lastPaidAt)}` : null}
              />
              <FactRow label="Awaiting payout" value={formatAmount(payouts.pending)} />
              <FactRow label="First sale" value={allTime.firstSaleAt ? formatDate(allTime.firstSaleAt) : "—"} />
              <FactRow label="Last sale" value={allTime.lastSaleAt ? formatDate(allTime.lastSaleAt) : "—"} />
            </DashboardCard>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <DashboardCard id="affiliate-programs" title="Programs" bodyClassName="px-3 pb-3">
              <DataTable
                columns={enrollmentColumns}
                data={enrollments}
                getRowId={(row) => row.programId}
                emptyText="Not in any of your programs"
                minTableWidth="min-w-0"
              />
            </DashboardCard>
            <DashboardCard id="affiliate-top-products" title="Top products" bodyClassName="px-3 pb-3">
              <DataTable
                columns={productColumns}
                data={data.topProducts}
                getRowId={(row) => row.productId}
                emptyText={`No product sales in the last ${days} days`}
                minTableWidth="min-w-0"
              />
            </DashboardCard>
          </div>
        </>
      )}
    </div>
  );
}
